import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { BankAppConfiguration } from '../../types/sdk';

type State = Required<Omit<BankAppConfiguration, 'longTermToken' | 'shortTermToken' | 'inviteToken' | 'url'>>;

type OptionalConfigProperties =
	| 'language'
	| 'primaryColor'
	| 'secondaryColor'
	| 'bankRedirectMethod'
	| 'autoClose'
	| 'autoCloseSeconds'
	| 'callbacks'
	| 'reuseSession';

type PartialConfiguration = Required<Pick<BankAppConfiguration, OptionalConfigProperties>>;

export const DEFAULT_CONFIGURATION: PartialConfiguration = {
	language: 'en',
	primaryColor: '#48b0ff',
	secondaryColor: '#c2e1f8',
	bankRedirectMethod: 'sameWindow',
	autoClose: true,
	autoCloseSeconds: 3,
	callbacks: {},
	reuseSession: false
};

const initialState: State = { ...DEFAULT_CONFIGURATION };

export const configurationSlice = createSlice({
	name: 'configuration',
	initialState,
	reducers: {
		saveConfiguration(_, action: PayloadAction<Partial<State>>) {
			const configuration = { ...action.payload };
			const reuseSession = isSessionReuse();
			return { ...DEFAULT_CONFIGURATION, ...configuration, reuseSession };
		}
	}
});

export const { saveConfiguration } = configurationSlice.actions;

const isSessionReuse = () => {
	const search = window.location.search;
	const params = new URLSearchParams(search);
	const redirect = params.get('redirect');
	return redirect === 'klarna';
};

export const selectConfiguration = (state: RootState) => state.configuration;

export const selectLanguage = (state: RootState) => {
	const configuration = selectConfiguration(state);
	return configuration.language;
};

export const selectPrimaryColor = (state: RootState) => {
	const configuration = selectConfiguration(state);
	return configuration.primaryColor;
};

export const selectSecondaryColor = (state: RootState) => {
	const configuration = selectConfiguration(state);
	return configuration.secondaryColor;
};

export const selectBankRedirectMethod = (state: RootState) => {
	const configuration = selectConfiguration(state);
	return configuration.bankRedirectMethod;
};

export const selectAutoCloseConfiguration = (state: RootState) => {
	const configuration = selectConfiguration(state);
	return { autoClose: configuration.autoClose, autoCloseSeconds: configuration.autoCloseSeconds };
};
